import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { resetUserInteraction } from "service/user-activity.service";

/**
 * CX-3744: clear the page-lifetime interaction latch on every route change.
 *
 * The session-expired dialog is meant to fire only on a user-driven auth
 * failure, using `hasUserInteracted()` to tell a real action apart from the
 * bootstrap requests that fire on mount. That latch never resets on its own, so
 * once the visitor clicks anything — even a public link like a trend-report
 * card — it stays armed, and the next page's on-mount 401s (e.g. the home page
 * re-fetching landing-page content) wrongly pop the login dialog.
 *
 * The reset runs during render, not in an effect: child effects flush before an
 * ancestor's effect, so a freshly mounted route's data-fetch effect would
 * snapshot the flag before an effect here could clear it. Guarding on the
 * previous pathname keeps it idempotent under StrictMode's double render.
 *
 * This assumes synchronous navigation: the app routes with a plain <Routes>, so
 * every rendered pathname is committed. Under concurrent routing (startTransition
 * or offscreen prerendering) React can render — and advance prevPathname here —
 * for a pathname it later discards, which would skip the reset on the real
 * navigation to that pathname and reintroduce the stale-latch pop. Revisit this
 * hook (drive the reset from a router navigation event instead) before adopting
 * concurrent routing.
 */
export const useResetInteractionOnRouteChange = (): void => {
  const { pathname } = useLocation();
  const prevPathname = useRef(pathname);
  if (prevPathname.current !== pathname) {
    prevPathname.current = pathname;
    resetUserInteraction();
  }
};
